import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { LoadingController, ToastController } from "ionic-angular";
import "rxjs/add/observable/throw";
import "rxjs/add/operator/catch";
import { Observable } from "rxjs/Observable";
import { Action } from "../../models/action";
import ContentWrapper from "../../models/content-wrapper/content-wrapper";
import Meeting from "../../models/idg/meeting";
import { Signature } from "../../models/signature";
import { ConfigurationService } from "../configuration/configuration.service";
import { StateManagementService } from "../state-management/state-management.service";

@Injectable()
export class IDGDataService {
  spinner = null;
  spinnerCounter = 0;

  idgDataUrl: string;
  idgSummaryUrl: string;
  idgUserId: string;
  apiUrl: string;

  orderManagementFeatureToggleOn: boolean;
  idgTempToggleOn: boolean;

  constructor(
    protected httpClient: HttpClient,
    public stateData: StateManagementService,
    public toastCtrl: ToastController,
    public loadingCtrl: LoadingController,
    private config: ConfigurationService
  ) {
    this.idgDataUrl = config.resolveIdgUrl();
    this.idgSummaryUrl = config.resolveIdgSummaryUrl();
    this.apiUrl = config.getConfig().apiUrl;
    this.orderManagementFeatureToggleOn =
      this.stateData.showOrderManagement.getValue();
    this.idgTempToggleOn = this.stateData.showIdgTempChanges.getValue();

    // todo: this needs to happen before the signature endpoint is called. will be an issue if /users is running slower than signatures happen
    if (!this.stateData.getIdgUserId()) {
      this.getIdgUserId().subscribe((data) => {
        // console.log(data.id);
        this.stateData.setIdgUserId(data.id);
        this.idgUserId = this.stateData.getIdgUserId();
      });
    }
  }

  public getIdgUserId(): Observable<any> {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    return this.httpClient
      .get(`${this.idgDataUrl}/company-resources/users`, { headers: headers })
      .catch((x) => this.handleException(x));
  }

  public initializeWebsocketConnectionForMeetingComments(
    meetingId: number,
    onMessage: Function,
    onError: Function
  ) {
    if (this.stateData.websocketIsInitializedComments()) {
      this.stateData.disconnectWebsocketConnectionComments();
    }

    return this.stateData.initializeWebsocketConnectionForMeetingComments(
      `${this.idgDataUrl}/gs-guide-websocket`,
      `/subscribe/meetings/${meetingId}/comments`,
      meetingId,
      onMessage,
      onError
    );
  }

  public subscribeComments(meetingId, onMessage: Function) {
    return this.stateData.subscribeComments(
      `/subscribe/meetings/${meetingId}/comments`,
      onMessage
    );
  }

  public getComments(
    meetingId: any,
    patientId: any
  ): Observable<ContentWrapper<Comment>> {
    return this.httpClient.get<ContentWrapper<Comment>>(
      `${this.idgDataUrl}/meetings/${meetingId}/comments?patientId=${patientId}`
    );
  }

  public makeComment(meetingId, patientId, applicationUserId, text: string) {
    return this.httpClient.post(
      `${this.idgDataUrl}/meetings/${meetingId}/comments`,
      {
        meeting: { id: +meetingId },
        patient: { id: +patientId },
        text: text,
        applicationUser: { id: +applicationUserId },
      }
    );
  }

  public initializeWebsocketConnectionForMeetingActions(
    meetingId: number,
    onMessage: Function,
    onError: Function
  ) {
    if (this.stateData.websocketIsInitializedActions()) {
      this.stateData.disconnectWebsocketConnectionActions();
    }

    return this.stateData.initializeWebsocketConnectionForMeetingActions(
      `${this.idgDataUrl}/gs-guide-websocket`,
      `/subscribe/meetings/${meetingId}/actions`,
      meetingId,
      onMessage,
      onError
    );
  }

  public initializeWebsocketConnectionForMeetingSignatures(
    meetingId: number,
    onMessage: Function,
    onError: Function
  ) {
    if (this.stateData.websocketIsInitialized()) {
      this.disconnectWebsocketConnection();
    }
    return this.stateData.initializeWebsocketConnectionForMeetingSignatures(
      `${this.idgDataUrl}/gs-guide-websocket`,
      `/subscribe/clinical/idg/meetings/${meetingId}/signatures`,
      meetingId,
      onMessage,
      onError
    );
  }

  // public sendSignatureOverWebsocket(signature) {
  public sendSignatureOverWebsocket(
    meetingId: number,
    patientId: number,
    role: string
  ) {
    let params = {};

    let roleCode = role;

    switch (roleCode) {
      case "registeredNurse":
        roleCode = "RN";
        break;
      case "caseManager":
        roleCode = "CM";
        break;
      case "teamLead":
        roleCode = "TL";
        break;
      case "medicalDirector":
        roleCode = "MD";
        break;
      case "socialWorker":
        roleCode = "SW";
        break;
      case "spiritualCare":
        roleCode = "SA";
        break;
      default:
        break;
    }

    let payload = {
      meeting: { id: +meetingId },
      patient: { id: +patientId },
      applicationUser: { id: +this.stateData.getIdgUserId() },
      role: roleCode,
    };

    if (this.stateData.websocketIsInitialized()) {
      let client = this.stateData.getWebsocketConnection();
      client.send(
        `/websocket/clinical/idg/meetings/${meetingId}/signatures`,
        params,
        JSON.stringify(payload)
      );
    } else {
      throw new Error("Websocket connection must be initialized first.");
    }
  }

  public getSignatures(meetingId: any): Observable<ContentWrapper<Signature>> {
    return this.httpClient.get<ContentWrapper<Signature>>(
      `${this.idgDataUrl}/meetings/${meetingId}/signatures`
    );
  }

  public createSignature(meetingId, patientId, role) {
    let body = {
      patient: { id: +patientId },
      meeting: { id: +meetingId },
      applicationUser: { id: +this.stateData.getIdgUserId() },
      role: role,
    };

    return this.httpClient
      .post(
        `${this.idgDataUrl}/meetings/${meetingId}/signatures?patient_id=${patientId}`,
        body
      )
      .catch((x) => this.handleException(x));
  }

  public createBulkSignature(meetingId, patientIds, roles) {
    let body = {
      patientIds: patientIds,
      roles: roles,
    };

    return this.httpClient
      .post(`${this.idgDataUrl}/meetings/${meetingId}/signatures`, body)
      .catch((x) => this.handleException(x));
  }

  public disconnectWebsocketConnection() {
    this.stateData.disconnectWebsocketConnection();
  }

  public disconnectWebsocketConnectionComments() {
    this.stateData.disconnectWebsocketConnectionComments();
  }

  public disconnectWebsocketConnectionActions() {
    this.stateData.disconnectWebsocketConnectionActions();
  }

  public getMeetings(
    limit: number,
    offset: number,
    search: string,
    sortColumn: string,
    sortOrder: string
  ): Observable<ContentWrapper<Meeting>> {
    let params = new HttpParams()
      .set("size", limit.toString())
      .set("page", offset.toString())
      .set("sort", `${sortColumn},${sortOrder}`)
      .set("search", search.toString());

    let headers = new HttpHeaders().set("Content-Type", "application/json");

    return this.httpClient
      .get<ContentWrapper<Meeting>>(`${this.idgDataUrl}/meetings`, {
        headers: headers,
        params: params,
      })
      .catch((x) => this.handleException(x));
  }

  public getMeeting(meetingId) {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    return this.httpClient
      .get(`${this.idgDataUrl}/meetings/${meetingId}`, { headers: headers })
      .catch((x) => this.handleException(x));
  }

  public getRecentAssessmentNotes(
    patientId,
    fromDate: string,
    toDate: string,
    idgTempToggle: boolean,
    sortBy: string = "createdDate",
    orderBy: "asc" | "desc" = "desc"
  ) {
    let headers = new HttpHeaders().set("Content-Type", "application/json");
    // patientId = 20668;
    return this.httpClient
      .get(
        `${this.apiUrl}/patient-charts/${patientId}/visits/recent-notes?fromDate=${fromDate}&toDate=${toDate}&idgTempToggle=${idgTempToggle}&sortBy=${sortBy}&sortOrder=${orderBy}`,
        { headers: headers }
      )
      .catch((x) => this.handleException(x));
  }

  public getAssessmentNotes(patientId) {
    let headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient
      .get(`${this.apiUrl}/patient-charts/${patientId}/visits/notes`, {
        headers: headers,
      })
      .catch((x) => this.handleException(x));
  }

  public getPatientDisplayData(patientId) {
    let headers = new HttpHeaders().set("Content-Type", "application/json");
    // patientId = 20668;
    return this.httpClient
      .get(`${this.apiUrl}/assessment/initialAndCurrentDetails/${patientId}`, {
        headers: headers,
      })
      .catch((x) => this.handleException(x));
  }

  public getMeetingAttendees(meetingId) {
    let params = new HttpParams().set("size", "9999");

    let headers = new HttpHeaders().set("Content-Type", "application/json");

    return this.httpClient
      .get(`${this.idgDataUrl}/meetings/${meetingId}/attendees`, {
        headers: headers,
        params: params,
      })
      .catch((x) => this.handleException(x));
  }

  public getMeetingUserInfo(meetingId) {
    return this.httpClient
      .get(`${this.idgDataUrl}/meetings/${meetingId}/attendees/users`)
      .catch((x) => this.handleException(x));
  }

  public getMeetingAttendee(meetingId, attendeeId) {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    return this.httpClient
      .get(
        `${this.idgDataUrl}/meetings/${meetingId}/attendees/${attendeeId}/roles`,
        { headers: headers }
      )
      .catch((x) => this.handleException(x));
  }

  public getMeetingPatients(meetingId): Observable<ContentWrapper<any>> {
    let params = new HttpParams().set("size", "9999").set("sort", "lastName");

    let headers = new HttpHeaders().set("Content-Type", "application/json");

    return this.httpClient
      .get<ContentWrapper<any>>(
        `${this.idgDataUrl}/meetings/${meetingId}/patients`,
        { headers: headers, params: params }
      )
      .catch((x) => this.handleException(x));
  }

  public getTeamsPatients(teamID) {
    console.log(teamID);

    let headers = new HttpHeaders().set("Content-Type", "application/json");

    let url = `${this.idgDataUrl}/company-resources/teams/${teamID}/patients?size=9999999`;
    if (this.idgTempToggleOn) {
      url += `&idgMeetingEligible=true`;
    }

    return this.httpClient
      .get(url, { headers: headers })
      .catch((x) => this.handleException(x));
  }

  public getPersonByQbosId(qbosId) {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    return this.httpClient
      .get(`${this.idgDataUrl}/company-resources/persons/qbosId/${qbosId}`, {
        headers: headers,
      })
      .catch((x) => this.handleException(x));
  }

  public getTeamMembers(teamID) {
    // console.log(teamID);

    let params = new HttpParams()
      .set("size", "9999")
      .set("sort", `lastName,asc`);

    let headers = new HttpHeaders().set("Content-Type", "application/json");

    return this.httpClient
      .get(`${this.idgDataUrl}/company-resources/teams/${teamID}/members`, {
        headers: headers,
        params: params,
      })
      .catch((x) => this.handleException(x));
  }

  public getPersons(search: string, showInactive: boolean = true) {
    let params = new HttpParams()
      .set("size", "9999")
      .set("search", search.toString())
      .set("showInactive", String(showInactive));

    let headers = new HttpHeaders().set("Content-Type", "application/json");

    return this.httpClient
      .get(`${this.idgDataUrl}/company-resources/persons`, {
        headers: headers,
        params: params,
      })
      .catch((x) => this.handleException(x));
  }

  public getRoles() {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    return this.httpClient
      .get(`${this.idgDataUrl}/enums/roles`, { headers: headers })
      .catch((x) => this.handleException(x));
  }

  public getTeams() {
    let params = new HttpParams().set("size", "9999");

    let headers = new HttpHeaders().set("Content-Type", "application/json");

    return this.httpClient
      .get(`${this.idgDataUrl}/company-resources/teams`, {
        headers: headers,
        params: params,
      })
      .catch((x) => this.handleException(x));
  }

  public createIDGMeeting(data, isPatientBenefitToggleOn: boolean) {
    console.log(data);
    return this.httpClient
      .post(
        `${this.idgDataUrl}/meetings?isPatientBenefitToggleOn=${isPatientBenefitToggleOn}`,
        data
      )
      .catch((x) => this.handleException(x));
  }

  public startIDGMeeting(meetingId, isPatientBenefitToggleOn: boolean) {
    return this.httpClient
      .put(
        `${this.idgDataUrl}/meetings/${meetingId}/start?isPatientBenefitToggleOn=${isPatientBenefitToggleOn}`,
        {}
      )
      .catch((x) => this.handleException(x));
  }

  public sendAttendees(data) {
    console.log(JSON.stringify(data.attendees, null, 2));
    return this.httpClient
      .put(`${this.idgDataUrl}/meetings/${data.meetingId}`, {
        attendees: data.attendees,
      })
      .catch((x) => this.handleException(x));
  }

  public addAction(action: string, meetingId, patientId, userId, role: string) {
    return this.httpClient
      .post(`${this.idgDataUrl}/meetings/${meetingId}/actions`, {
        action,
        meeting: { id: meetingId },
        patient: { id: patientId },
        applicationUser: { id: userId },
        role,
      })
      .catch((x) => this.handleException(x));
  }

  public getActions(meetingId): Observable<ContentWrapper<Action>> {
    let params = new HttpParams().set("size", "9999");

    let headers = new HttpHeaders().set("Content-Type", "application/json");

    return this.httpClient
      .get<ContentWrapper<Action>>(
        `${this.idgDataUrl}/meetings/${meetingId}/actions`,
        { headers: headers, params: params }
      )
      .catch((x) => this.handleException(x));
  }

  public closeIDGMeeting(meetingId) {
    let url = `${this.idgDataUrl}/meetings/${meetingId}/close`;
    if (this.orderManagementFeatureToggleOn == true) {
      url += "?orderMgt=true";
    } else {
      url += "?orderMgt=false";
    }
    return new Observable((observer) => {
      this.increaseSpinnerCount("Closing IDG Meeting ...");
      return this.httpClient
        .put(url, {})
        .catch((x) => this.handleException(x))
        .subscribe(
          (data) => {
            //this.presentSuccessToast("Successfully updated record data.");
            observer.next(data);
            observer.complete();
            this.decreaseSpinnerCount();
          },
          (err) => {
            //this.presentErrorToast(err, "Failed to update record.");
            this.decreaseSpinnerCount();
            observer.error(err);
            observer.complete();
          }
        );
    });

    // return this.httpClient.put(`${this.idgDataUrl}/meetings/${meetingId}/close`, {})
    //   .catch(x => this.handleException(x))
  }

  public printIDGMeeting(meetingId) {
    return this.httpClient
      .get(`${this.idgDataUrl}/meetings/${meetingId}/generate_report`, {
        responseType: "arraybuffer",
      })
      .catch((x) => this.handleException(x));
  }

  public getPrnVisitsAC(patientChartID) {
    return this.httpClient
      .get(
        `${this.idgDataUrl}/orders/patient-charts/${patientChartID}/prnVisits?status=AC`
      )
      .catch((x) => this.handleException(x));
  }

  public getClosedIDGMeetings(patientChartId) {
    return this.httpClient
      .get(
        `${this.idgDataUrl}/patient-charts/${patientChartId}/closed-meetings`
      )
      .catch((x) => this.handleException(x));
  }

  // TODO: Handle more gracefully
  protected handleException(exception: any) {
    let message = `${exception.status} : ${exception.statusText}\n${
      exception.body ? exception.body.error : ""
    }`;
    console.log(exception);
    console.log(message);
    return Observable.throw(exception);
  }

  presentSuccessToast(message: string) {
    let toast = this.toastCtrl.create({
      message: message,
      duration: 3000,
      position: "below",
      cssClass: "success",
    });

    toast.present();
  }

  presentErrorToast(err: HttpErrorResponse, message: string) {
    if (err && (err as any).code === "REFRESH_TOKEN_FAILED") {
      return;
    }
    let toast = this.toastCtrl.create({
      message: message,
      duration: 4000,
      position: "below",
      cssClass: "error",
    });

    toast.present();
  }

  increaseSpinnerCount(message: string = "Loading data...") {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  decreaseSpinnerCount() {
    // console.log( "Decreasing spinner count.", this.spinnerCounter );
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  public editDiscussionNote(payload) {
    return this.httpClient.put(
      `${this.idgDataUrl}/meetings/${payload["meetingId"]}/comments/${payload["id"]}`,
      {
        meeting: { id: payload["meetingId"] },
        patient: { id: payload["patChartId"] },
        text: payload["commentInput"],
        applicationUser: { id: payload["idgUserId"] },
        createdDatetime: payload["createdDatetime"],
      }
    );
  }

  public deleteDiscussionNote(meetingId, commentId) {
    let url = `${this.idgDataUrl}/meetings/${meetingId}/comments/${commentId}`;
    return this.httpClient.delete(url, {
      headers: new HttpHeaders().set("Content-Type", "application/json"),
    });
  }

  public updateMeeting(meeting: Meeting) {
    const url = `${this.idgDataUrl}/meetings/${meeting.id}`;
    return this.httpClient.put(url, JSON.stringify(meeting), {
      headers: new HttpHeaders().set("Content-Type", "application/json"),
    });
  }

  public deleteMeeting(meeting: Meeting) {
    const url = `${this.idgDataUrl}/meetings/${meeting.id}`;
    return this.httpClient.delete(url, {
      headers: new HttpHeaders().set("Content-Type", "application/json"),
    });
  }

  public triggerIDGSummary(meetingId: number) {
    return this.httpClient
      .post(`${this.idgSummaryUrl}/meetings/${meetingId}/summary`, {})
      .catch((x) => this.handleException(x));
  }

  // NOVA-1921: per-patient prep regeneration. The meeting-wide endpoint above
  // resets EVERY patient's prep (~1hr for a full roster); the manual per-patient
  // "Refresh Prep" button must use this single-patient endpoint instead.
  // patientChartId is the patient's qbosId (same id the GET summary call uses).
  public triggerIDGSummaryForPatient(meetingId: number, patientChartId: number) {
    return this.httpClient
      .post(
        `${this.idgSummaryUrl}/meetings/${meetingId}/${patientChartId}/summary`,
        {}
      )
      .catch((x) => this.handleException(x));
  }

  public getSummaryStatus(meetingId: number) {
    return this.httpClient
      .get(`${this.idgSummaryUrl}/meetings/${meetingId}/summary/status`)
      .catch((x) => this.handleException(x));
  }

  public deleteSummary(meetingId: number, patientChartId: number) {
    const url = `${this.idgSummaryUrl}/meetings/${meetingId}/${patientChartId}/summary`;
    return this.httpClient.request("delete", url);
  }

  public faxIdgMeetting(request: any, meetingId: number): Observable<any> {
    return this.httpClient
      .post(`${this.idgDataUrl}/meetings/${meetingId}/fax`, request)
      .catch((x) => this.handleException(x));
  }
}



// WEBPACK FOOTER //
// ./src/providers/idg-data-service/idg-data-service.ts